import type { TestResult } from '../types';
import { computeStats, now } from '../utils';

const GROUP = 'L_storage';

type IndexedDbBatchResult = {
  writeSamples: number[];
  readSamples: number[];
  deleteSamples: number[];
  wallMs: number;
  sizeBytes: number;
  batch: number;
  runs: number;
};

function trimExtremes(samples: number[]): number[] {
  if (samples.length < 5) return samples;
  const sorted = [...samples].sort((a, b) => a - b);
  return sorted.slice(1, -1);
}

function makeBytes(sizeBytes: number): ArrayBuffer {
  const bytes = new Uint8Array(sizeBytes);
  let seed = 123456789;
  for (let i = 0; i < bytes.length; i++) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    bytes[i] = seed & 0xff;
  }
  return bytes.buffer;
}

function openDb(name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB open failed'));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
  });
}

async function putValue(db: IDBDatabase, key: string, value: ArrayBuffer): Promise<void> {
  const tx = db.transaction('kv', 'readwrite');
  tx.objectStore('kv').put(value, key);
  await txDone(tx);
}

function getValue(db: IDBDatabase, key: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction('kv', 'readonly');
    const request = tx.objectStore('kv').get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB read failed'));
  });
}

async function deleteValue(db: IDBDatabase, key: string): Promise<void> {
  const tx = db.transaction('kv', 'readwrite');
  tx.objectStore('kv').delete(key);
  await txDone(tx);
}

function deleteDb(name: string): Promise<void> {
  return new Promise((resolve) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}

async function measureIndexedDbBatch(options: { sizeBytes: number; batch: number; runs: number; warmup: number }): Promise<IndexedDbBatchResult> {
  const { sizeBytes, batch, runs, warmup } = options;
  const dbName = `pipa-storage-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const payload = makeBytes(sizeBytes);
  const db = await openDb(dbName);

  try {
    for (let i = 0; i < warmup; i++) {
      for (let j = 0; j < batch; j++) {
        const key = `warmup-${i}-${j}`;
        await putValue(db, key, payload);
        await getValue(db, key);
        await deleteValue(db, key);
      }
    }

    const writeSamples: number[] = [];
    const readSamples: number[] = [];
    const deleteSamples: number[] = [];
    const wallStart = now();

    for (let i = 0; i < runs; i++) {
      const keys: string[] = [];
      let t0 = now();
      for (let j = 0; j < batch; j++) {
        const key = `run-${i}-${j}`;
        keys.push(key);
        await putValue(db, key, payload);
      }
      writeSamples.push((now() - t0) / batch);

      t0 = now();
      for (const key of keys) await getValue(db, key);
      readSamples.push((now() - t0) / batch);

      t0 = now();
      for (const key of keys) await deleteValue(db, key);
      deleteSamples.push((now() - t0) / batch);
    }

    return { writeSamples, readSamples, deleteSamples, wallMs: now() - wallStart, sizeBytes, batch, runs };
  } finally {
    db.close();
    await deleteDb(dbName);
  }
}

function makeTimedResult(name: string, samples: number[], metadata: Record<string, unknown>): TestResult {
  const trimmedSamples = trimExtremes(samples);
  const batch = typeof metadata.batch === 'number' ? metadata.batch : 1;
  return {
    name,
    group: GROUP,
    success: true,
    durationMs: samples.reduce((sum, value) => sum + value, 0) * batch,
    samples,
    stats: computeStats(trimmedSamples),
    metadata: { ...metadata, rawSamples: samples, trimmedSamples },
  };
}

export async function runStorageTests(): Promise<TestResult[]> {
  const tests: TestResult[] = [];
  const started = now();

  if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
    try {
      const t0 = now();
      const estimate = await navigator.storage.estimate();
      let persisted: boolean | string | null = null;
      if (navigator.storage.persisted) {
        try { persisted = await navigator.storage.persisted(); }
        catch (err) { persisted = `error: ${err instanceof Error ? err.message : String(err)}`; }
      }
      tests.push({
        name: 'storage_estimate',
        group: GROUP,
        success: true,
        durationMs: now() - t0,
        metadata: {
          quota: estimate.quota ?? null,
          usage: estimate.usage ?? null,
          usageDetails: (estimate as StorageEstimate & { usageDetails?: Record<string, number> }).usageDetails ?? null,
          persisted,
        },
      });
    } catch (err) {
      tests.push({ name: 'storage_estimate', group: GROUP, success: false, durationMs: now() - started, error: err instanceof Error ? err.message : String(err) });
    }
  } else {
    tests.push({ name: 'storage_estimate', group: GROUP, success: false, durationMs: 0, error: 'navigator.storage.estimate is not available' });
  }

  if (typeof indexedDB === 'undefined') {
    tests.push({ name: 'indexeddb_unavailable', group: GROUP, success: false, durationMs: now() - started, error: 'IndexedDB is not available' });
    return tests;
  }

  try {
    // 1MB is large enough to expose storage backend differences, but with batch=2/runs=7 stays fast (<~100ms on tested Chromium).
    const sizeBytes = 1024 * 1024;
    const batch = 2;
    const runs = 7;
    const measured = await measureIndexedDbBatch({ sizeBytes, batch, runs, warmup: 1 });
    const common = { sizeBytes, batch, runs, wallMs: measured.wallMs };
    tests.push(makeTimedResult('indexeddb_write_1mb', measured.writeSamples, common));
    tests.push(makeTimedResult('indexeddb_read_1mb', measured.readSamples, common));
    tests.push(makeTimedResult('indexeddb_delete_1mb', measured.deleteSamples, common));
  } catch (err) {
    tests.push({ name: 'indexeddb_1mb', group: GROUP, success: false, durationMs: now() - started, error: err instanceof Error ? err.message : String(err) });
  }

  return tests;
}
